import { Component } from '@theme/component';
import {
  CustomerAccountError,
  REPLAY_KEY,
  beginAuth,
  graphql,
} from '@theme/customer-account';

const BOOKMARK_PREFIX = 'course-bookmark:';
const ACCESS_REFRESH_PREFIX = 'course-access-refresh:';
const QUIZ_KEY_PREFIX = 'quiz:';
const LOCAL_PROGRESS_PREFIX = 'course-progress:v2:';
const LEGACY_LOCAL_PROGRESS_PREFIX = 'course-progress:';
const MAX_PROGRESS_WRITE_ATTEMPTS = 3;
const PROGRESS_MAX_BYTES = 128 * 1024;

/**
 * Publish through Shopify when the theme analytics bridge is available, and always mirror the
 * event as a DOM event so a pixel or tag manager can subscribe without changing course code.
 *
 * @param {HTMLElement} root
 * @param {string} name
 * @param {string} position
 */
function publishCourseAnalytics(root, name, position = '') {
  if (!name) return;
  const detail = {
    product_id: root.dataset.courseKey ?? '',
    product_handle: root.dataset.legacyCourseKey ?? '',
    surface: root.matches('course-index') ? 'course_index' : 'course_lesson',
    cta_position: position,
  };

  const published = window.Shopify?.analytics?.publish?.(`monarch_course:${name}`, detail);
  published?.catch?.(() => {});
  root.dispatchEvent(new CustomEvent(`course:${name}`, { bubbles: true, detail }));
}

/**
 * Completion uses the same nested document shape in both persistence modes:
 *
 *   { "<courseKey>": { "<moduleKey>": true } }
 *
 * When a Headless Customer Account API client ID is configured, the customer metafield remains the
 * authoritative cross-device copy. Without a client ID, the exact same document is stored in
 * localStorage so Mark as Complete and quiz scores still work in this browser. Keeping the shape
 * identical lets a later Headless rollout merge browser progress forward instead of discarding it.
 */
const PROGRESS_MUTATION = `
  mutation SetCourseProgress($metafields: [MetafieldsSetInput!]!) {
    metafieldsSet(metafields: $metafields) {
      metafields {
        key
        namespace
        compareDigest
      }
      userErrors {
        field
        message
        code
      }
    }
  }
`;

const PROGRESS_QUERY = `
  query CourseProgress {
    customer {
      id
      metafield(namespace: "course", key: "progress") {
        value
        compareDigest
      }
    }
  }
`;

/**
 * @param {string} raw JSON from `data-progress`.
 * @returns {Record<string, Record<string, boolean>>}
 */
function parseProgress(raw) {
  try {
    const value = JSON.parse(raw || 'null');
    // An UNSET json metafield renders as the literal `null`, not `{}` — so this cannot just check
    // for a missing attribute.
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    return value;
  } catch {
    return {};
  }
}

/** @param {string} raw @returns {Record<string, string>} */
function parseModuleKeyMap(raw) {
  try {
    const value = JSON.parse(raw || '{}');
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    return Object.fromEntries(
      Object.entries(value)
        .map(([stable, legacy]) => [String(stable), String(legacy)])
        .filter(([stable, legacy]) => stable && legacy)
    );
  } catch {
    return {};
  }
}

const owns = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

/**
 * Converts one course from editable handle keys to immutable Shopify IDs and removes module keys
 * which are no longer in that product's ordered module list. Other courses are left untouched.
 *
 * @param {Record<string, any>} all
 * @param {{ courseKey: string, legacyCourseKey: string, moduleKeyMap: Record<string, string> }} keys
 */
function normalizeProgress(all, { courseKey, legacyCourseKey, moduleKeyMap }) {
  // An empty map means the ordered module list did not render — a module whose `course` reference
  // is unset, a course product with no `course_modules`, or malformed `data-module-key-map` — not
  // that the course genuinely has no modules. Pruning against it would compact every course to `{}`,
  // and `readLocalProgress` writes its result straight back, so a single page view would erase every
  // stored tick. Leave the document untouched until the map can be trusted.
  if (!Object.keys(moduleKeyMap).length) return { ...all };

  const next = { ...all };
  const stableState = all[courseKey] && typeof all[courseKey] === 'object' ? all[courseKey] : {};
  const legacyState =
    legacyCourseKey && all[legacyCourseKey] && typeof all[legacyCourseKey] === 'object'
      ? all[legacyCourseKey]
      : {};
  const compact = {};

  for (const [stableModuleKey, legacyModuleKey] of Object.entries(moduleKeyMap)) {
    const done = owns(stableState, stableModuleKey)
      ? stableState[stableModuleKey]
      : legacyState[legacyModuleKey];
    if (done === true) compact[stableModuleKey] = true;
  }

  next[courseKey] = compact;
  if (legacyCourseKey && legacyCourseKey !== courseKey) delete next[legacyCourseKey];

  const quizKey = QUIZ_KEY_PREFIX + courseKey;
  const legacyQuizKey = QUIZ_KEY_PREFIX + legacyCourseKey;
  if (!owns(next, quizKey) && legacyCourseKey && owns(next, legacyQuizKey)) {
    next[quizKey] = next[legacyQuizKey];
  }
  if (legacyCourseKey && legacyQuizKey !== quizKey) delete next[legacyQuizKey];

  return next;
}

/** @param {string} scope */
function localProgressKey(scope) {
  return LOCAL_PROGRESS_PREFIX + (scope || 'guest');
}

/**
 * @param {string} scope
 * @param {{ courseKey: string, legacyCourseKey: string, moduleKeyMap: Record<string, string> }} keys
 */
function readLocalProgress(scope, keys) {
  let all = {};

  try {
    all = parseProgress(localStorage.getItem(localProgressKey(scope)) ?? '');

    // Revision 1 stored one flat module map per course handle. Import it once into the nested,
    // stable-ID document used by Revision 2, then remove the obsolete key.
    const legacyStorageKeys = [keys.courseKey, keys.legacyCourseKey].filter(Boolean);
    for (const key of legacyStorageKeys) {
      const oldKey = LEGACY_LOCAL_PROGRESS_PREFIX + key;
      const legacy = parseProgress(localStorage.getItem(oldKey) ?? '');
      if (Object.keys(legacy).length && !all[key]) all[key] = legacy;
      localStorage.removeItem(oldKey);
    }
  } catch {
    return {};
  }

  const normalized = normalizeProgress(all, keys);
  writeLocalProgress(scope, normalized);
  return normalized;
}

/**
 * Merge completed-module booleans without allowing a stale browser copy to erase an account copy.
 * For quiz scores, the primary document wins; the secondary is only a migration fallback.
 *
 * @param {Record<string, any>} primary
 * @param {Record<string, any>} secondary
 * @param {{ courseKey: string, legacyCourseKey: string, moduleKeyMap: Record<string, string> }} keys
 */
function mergeProgress(primary, secondary, keys) {
  const first = normalizeProgress(primary, keys);
  const second = normalizeProgress(secondary, keys);
  const merged = { ...second, ...first };

  merged[keys.courseKey] = {
    ...(second[keys.courseKey] ?? {}),
    ...(first[keys.courseKey] ?? {}),
  };

  const quizKey = QUIZ_KEY_PREFIX + keys.courseKey;
  if (!owns(first, quizKey) && owns(second, quizKey)) merged[quizKey] = second[quizKey];

  return merged;
}

/**
 * @param {string} scope
 * @param {Record<string, any>} all
 * @returns {boolean}
 */
function writeLocalProgress(scope, all) {
  try {
    const value = JSON.stringify(all);
    if (new TextEncoder().encode(value).byteLength > PROGRESS_MAX_BYTES) return false;
    localStorage.setItem(localProgressKey(scope), value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Reads the latest document, applies one small mutation, then writes it with compare-and-set.
 * A stale digest re-reads and merges again, so two tabs cannot erase one another's course data.
 *
 * @param {{ clientId: string, mutate: (all: Record<string, any>) => Record<string, any> }} options
 * @returns {Promise<Record<string, any>>}
 */
async function persistProgress({ clientId, mutate }) {
  for (let attempt = 0; attempt < MAX_PROGRESS_WRITE_ATTEMPTS; attempt += 1) {
    const snapshot = await graphql({ clientId, query: PROGRESS_QUERY });
    const ownerId = snapshot?.customer?.id;
    if (!ownerId) {
      throw new CustomerAccountError('Customer Account API returned no customer id.', {
        code: 'NO_CUSTOMER',
      });
    }

    const metafield = snapshot.customer.metafield;
    const next = mutate(parseProgress(metafield?.value ?? ''));
    const value = JSON.stringify(next);
    if (new TextEncoder().encode(value).byteLength > PROGRESS_MAX_BYTES) {
      throw new CustomerAccountError('Course progress has reached Shopify’s metafield size limit.', {
        code: 'TOO_LONG',
      });
    }

    const data = await graphql({
      clientId,
      query: PROGRESS_MUTATION,
      variables: {
        metafields: [
          {
            ownerId,
            namespace: 'course',
            key: 'progress',
            type: 'json',
            value,
            compareDigest: metafield?.compareDigest ?? null,
          },
        ],
      },
    });

    const [failure] = data?.metafieldsSet?.userErrors ?? [];
    if (!failure) return next;

    if (
      (failure.code === 'INVALID_COMPARE_DIGEST' || failure.code === 'STALE_OBJECT') &&
      attempt + 1 < MAX_PROGRESS_WRITE_ATTEMPTS
    ) {
      continue;
    }

    throw new CustomerAccountError(failure.message ?? 'Progress could not be saved.', {
      code: failure.code ?? 'USER_ERROR',
    });
  }

  throw new CustomerAccountError('Progress changed in another tab. Please try again.', {
    code: 'STALE_OBJECT',
  });
}

/**
 * The most recently visited section in a course. Kept separate from completion state so the
 * existing progress format stays backwards-compatible and each concern can fail independently.
 *
 * @typedef {object} CourseBookmark
 * @property {string} moduleKey
 * @property {string} moduleUrl
 * @property {string} sectionId
 * @property {string} label
 * @property {number} updatedAt
 */

/**
 * @param {string} courseKey
 * @returns {CourseBookmark | null}
 */
function readBookmark(courseKey) {
  try {
    const value = JSON.parse(localStorage.getItem(BOOKMARK_PREFIX + courseKey) ?? 'null');
    if (
      !value ||
      typeof value.moduleKey !== 'string' || !value.moduleKey ||
      typeof value.moduleUrl !== 'string' || !value.moduleUrl ||
      typeof value.sectionId !== 'string' || !value.sectionId ||
      typeof value.label !== 'string' || !value.label ||
      typeof value.updatedAt !== 'number'
    ) {
      return null;
    }

    return value;
  } catch {
    return null;
  }
}

/**
 * @param {string} courseKey
 * @param {CourseBookmark} bookmark
 */
function writeBookmark(courseKey, bookmark) {
  try {
    localStorage.setItem(BOOKMARK_PREFIX + courseKey, JSON.stringify(bookmark));
  } catch {
    // Storage full or blocked. Course navigation continues to work without persistence.
  }
}

/** @param {string} courseKey */
function dropBookmark(courseKey) {
  try {
    localStorage.removeItem(BOOKMARK_PREFIX + courseKey);
  } catch {
    // Best effort migration cleanup.
  }
}

/**
 * The label a lesson section contributes to the "On this page" rail and to the resume bookmark.
 *
 * Revision 1 authored sections as theme blocks, which always emitted `data-toc-label`. Revision 2
 * authors them as HTML in a metaobject field, copied from the design mockups — where the label
 * lives in the section's own <h2> and no such attribute exists. Falling straight through to the id
 * would print "why-setup" where the design reads "Why This Module Exists", and closing that gap by
 * hand would mean adding the attribute to roughly a hundred sections across seventeen modules.
 *
 * The id stays as the last resort, so a section with neither an attribute nor a heading still gets
 * a usable entry rather than an empty link.
 *
 * @param {HTMLElement} section
 * @returns {string}
 */
function sectionLabel(section) {
  const authored = section.dataset.tocLabel || section.querySelector('h2')?.textContent?.trim();
  if (authored) return authored;

  // Last resort. Not every authored section leads with an h2 — the closing "Your Move" block titles
  // itself with a styled div — and the id fell through to the reader verbatim as "your-move".
  // Title-casing the slug is still a guess, but it is a legible one.
  return section.id
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase())
    .trim();
}

/**
 * @typedef {object} Refs
 * @property {HTMLElement} [tocListDesktop]
 * @property {HTMLElement} [tocListInline]
 * @property {HTMLElement[]} [progressBar]
 * @property {HTMLElement[]} [progressFill]
 * @property {HTMLElement[]} [progressLabel]
 * @property {HTMLButtonElement} [completeButtonDesktop]
 * @property {HTMLButtonElement} [completeButtonInline]
 * @property {HTMLElement[]} [completeError]
 * @property {HTMLAnchorElement} [resumeLink]
 * @property {HTMLElement} [resumeLabel]
 * @property {HTMLElement} [lockedPanel]
 */

/** @extends {Component<Refs>} */
class CourseModule extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {Set<string>} */
  #visible = new Set();

  /** @type {string[]} */
  #sectionIds = [];

  /** @type {string | null} */
  #activeSectionId = null;

  /** Prevents the observer's initial paint from replacing a useful saved position. */
  #hasObservedInitialSection = false;

  /** @type {number | undefined} */
  #accessRefreshTimer;

  /**
   * The whole metafield object, every course. Kept because the mutation replaces the metafield
   * wholesale — writing back only this course's slice would delete the customer's other courses.
   *
   * @type {Record<string, Record<string, boolean>>}
   */
  #allProgress = {};

  /**
   * This course's slice, and the only thing the UI paints from. In-memory state IS the source of
   * truth between writes: #renderProgress used to re-read storage on every call, which would
   * overwrite an optimistic paint with stale data the moment the write became asynchronous.
   *
   * @type {Record<string, boolean>}
   */
  #progress = {};

  /** Immutable module ID -> legacy handle, emitted from the product's current ordered list. */
  #moduleKeyMap = {};

  /** Only these keys are allowed to contribute to the progress denominator. */
  #moduleKeys = [];

  /** One write at a time. A double-click would otherwise race two full-object writes. */
  #saving = false;

  #onStorage = (event) => {
    if (event.key !== localProgressKey(this.#progressScope)) return;
    this.#hydrateProgress();
    this.#renderProgress();
  };

  #onAnalyticsClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-course-event]') : null;
    if (!(target instanceof HTMLElement) || !this.contains(target)) return;
    publishCourseAnalytics(this, target.dataset.courseEvent ?? '', target.dataset.coursePosition ?? '');
  };

  connectedCallback() {
    super.connectedCallback();

    this.#hydrateProgress();
    this.#render();
    this.#replayPendingToggle();
    this.#scheduleAccessRefresh();
    window.addEventListener('storage', this.#onStorage);
    this.addEventListener('click', this.#onAnalyticsClick);

    // A custom element is upgraded the moment its START tag is parsed, so connectedCallback can
    // run before any of its children exist. When that happens the refs are empty, the TOC lists
    // are missing, and everything below paints nothing — permanently, because the base class's
    // MutationObserver refreshes `refs` but never re-runs us. Horizon hits the same race in
    // critical.js (OverflowList) and works around it the same way.
    //
    // #render is idempotent, so re-running it once the parser is done is free.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.updatedCallback(), { once: true });
    }
  }

  // The TOC, progress bar, resume link and sidebar ticks exist only in JS — the server ships
  // them empty. Without this, every settings edit in the theme editor re-renders the section
  // and leaves them blank until a hard reload, which is exactly the authoring workflow.
  updatedCallback() {
    super.updatedCallback?.();
    this.#render();
  }

  /** Everything that paints from the DOM. Safe to run any number of times. */
  #render() {
    this.#observer?.disconnect();
    this.#visible.clear();
    this.#hasObservedInitialSection = false;

    this.#buildToc();
    this.#activatePathCards();
    this.#placeContentEnhancements();
    this.#renderProgress();
    this.#renderResume();
    this.#observeSections();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#observer?.disconnect();
    if (this.#accessRefreshTimer) window.clearTimeout(this.#accessRefreshTimer);
    window.removeEventListener('storage', this.#onStorage);
    this.removeEventListener('click', this.#onAnalyticsClick);
  }

  get #courseKey() {
    return this.dataset.courseKey ?? 'course';
  }

  get #moduleKey() {
    return this.dataset.moduleKey ?? '';
  }

  get #moduleUrl() {
    return this.dataset.moduleUrl ?? '';
  }

  get #legacyCourseKey() {
    return this.dataset.legacyCourseKey ?? '';
  }

  get #legacyModuleKey() {
    return this.dataset.legacyModuleKey ?? '';
  }

  get #clientId() {
    return this.dataset.accountClientId ?? '';
  }

  get #progressScope() {
    return this.dataset.progressScope ?? 'guest';
  }

  get #progressKeys() {
    return {
      courseKey: this.#courseKey,
      legacyCourseKey: this.#legacyCourseKey,
      moduleKeyMap: this.#moduleKeyMap,
    };
  }

  /**
   * The theme editor renders the complete button (see the comment beside it in the section — hiding
   * it would leave the merchant authoring a course with no visible completion control), but there
   * is no signed-in customer to write for, so the button must be inert rather than error.
   * theme.liquid puts this class on <html>.
   */
  get #isDesignMode() {
    return document.documentElement.classList.contains('shopify-design-mode');
  }

  /**
   * Merge the server-rendered account copy with this browser's fallback. Account data wins quiz
   * conflicts; completed modules are unioned so enabling Headless later never loses local ticks.
   */
  #hydrateProgress() {
    this.#moduleKeyMap = parseModuleKeyMap(this.dataset.moduleKeyMap ?? '');
    this.#moduleKeys = Object.keys(this.#moduleKeyMap);
    const server = parseProgress(this.dataset.progress ?? '');
    const local = readLocalProgress(this.#progressScope, this.#progressKeys);
    this.#allProgress = mergeProgress(server, local, this.#progressKeys);
    this.#progress = this.#allProgress[this.#courseKey] ?? {};
  }

  /** Migrate the browser-local resume point alongside account progress. */
  #readCurrentBookmark() {
    const current = readBookmark(this.#courseKey);
    if (current || !this.#legacyCourseKey) return current;

    const legacy = readBookmark(this.#legacyCourseKey);
    if (!legacy) return null;
    const stableModuleKey = Object.entries(this.#moduleKeyMap)
      .find(([, legacyModuleKey]) => legacyModuleKey === legacy.moduleKey)?.[0];
    if (!stableModuleKey) return null;

    const migrated = { ...legacy, moduleKey: stableModuleKey };
    writeBookmark(this.#courseKey, migrated);
    dropBookmark(this.#legacyCourseKey);
    return migrated;
  }

  /** Restores Module 0's learning-path selector without importing arbitrary mockup scripts. */
  #activatePathCards() {
    const cards = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.path-card')]);
    for (const card of cards) {
      if (card.dataset.coursePathBound === 'true') continue;
      card.dataset.coursePathBound = 'true';
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
      card.setAttribute('aria-pressed', String(card.classList.contains('selected')));

      const select = () => {
        for (const sibling of cards) {
          sibling.classList.toggle('selected', sibling === card);
          sibling.setAttribute('aria-pressed', String(sibling === card));
        }
      };
      card.addEventListener('click', select);
      card.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        select();
      });
    }
  }

  /** Put theme-owned video/tool components where their removed mockup placeholders originally sat. */
  #placeContentEnhancements() {
    const videoSlot = this.querySelector('[data-course-video-slot]');
    const video = this.querySelector('.course-video');
    if (videoSlot && video && !videoSlot.contains(video)) videoSlot.append(video);

    // All three shapes a tool can arrive in. `.course-tool` covers the serp and ctr blocks;
    // `.course-calc` is the compounding calculator, which names itself differently because it
    // reuses that stylesheet; `course-calc` is the per-module calculator snippet, which carries no
    // class at all so it does not pick up .course-tool's border on top of its own.
    //
    // Matching only `.course-tool` meant the calculator was never moved into its slot and rendered
    // at the very bottom of the lesson instead of where the mockup's widget sat.
    //
    // Pairwise, because a lesson may author more than one: the Legal System's capstone has a
    // demand-letter generator in one section and a readiness checklist in another. Matching a
    // single slot to a single tool put the second widget at the foot of the page.
    const toolSlots = this.querySelectorAll('[data-course-tool-slot]');
    const tools = this.querySelectorAll('.course-tool, .course-calc, course-calc');
    toolSlots.forEach((slot, index) => {
      const tool = tools[index];
      if (tool && !slot.contains(tool)) slot.append(tool);
    });
  }

  /**
   * Applies the tick that triggered sign-in, once the OAuth round trip lands back here. The click
   * happened before the redirect; without this it is silently lost and the customer has to press
   * the button a second time for no visible reason.
   */
  #replayPendingToggle() {
    let pending;

    try {
      const raw = sessionStorage.getItem(REPLAY_KEY);
      if (!raw) return;
      // Consume it first. A parse failure or a mismatched page must not leave it queued to fire on
      // some unrelated later page view.
      sessionStorage.removeItem(REPLAY_KEY);
      pending = JSON.parse(raw);
    } catch {
      return;
    }

    const matchingCourse = pending?.courseKey === this.#courseKey || pending?.courseKey === this.#legacyCourseKey;
    const matchingModule = pending?.moduleKey === this.#moduleKey || pending?.moduleKey === this.#legacyModuleKey;
    if (!matchingCourse || !matchingModule) return;

    // Already in the wanted state — the write landed before the redirect, or the customer got here
    // another way. Toggling now would undo it.
    if (Boolean(this.#progress[this.#moduleKey]) === Boolean(pending.done)) return;

    this.toggleComplete();
  }

  /** The right-hand "On this page" rail, built from the lesson sections actually rendered. */
  #buildToc() {
    // `[id]` also matches id="", and CSS.escape('') would later build the invalid selector '#'.
    //
    // Two class names, one meaning. `.course-lesson-section` is what Revision 1's section BLOCK
    // emits; `.lesson-section` is what the design mockups use, and Revision 2 pastes their markup
    // into a metaobject field verbatim. Accepting both is one selector here versus a find-and-
    // replace over every authored module, and it keeps already-authored Revision 1 pages working.
    // The query is scoped to `this` — the <course-module> element — so neither name can reach
    // anything else on the store.
    const sections = /** @type {HTMLElement[]} */ ([
      ...this.querySelectorAll('.course-lesson-section[id], .lesson-section[id]'),
    ]).filter((section) => section.id);

    // Recorded before the TOC containers are checked: scrollspy and the resume bookmark depend on
    // these, and they should keep working on a layout that ships no "On this page" rail at all.
    this.#sectionIds = sections.map((section) => section.id);

    const lists = [this.refs.tocListDesktop, this.refs.tocListInline].filter(Boolean);
    if (!lists.length) return;

    for (const list of lists) {
      list.replaceChildren(); // Idempotent: updatedCallback re-runs this on every editor re-render.

      for (const section of sections) {
        const link = document.createElement('a');
        link.className = 'course-toc__item';
        link.href = `#${section.id}`;
        link.textContent = sectionLabel(section);
        link.setAttribute('on:click', '/rememberSection');
        list?.append(link);
      }
    }
  }

  #observeSections() {
    if (!this.#sectionIds.length) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#visible.add(entry.target.id);
          else this.#visible.delete(entry.target.id);
        }

        // The reference activates whichever entry happens to come last in the callback array,
        // so with several sections on screen the highlight lands arbitrarily and scrolling up
        // never restores the section above. Always take the first VISIBLE section in DOM order.
        const activeId = this.#sectionIds.find((id) => this.#visible.has(id));
        if (!activeId) return;

        this.#setActiveTocItem(activeId);

        if (!this.#hasObservedInitialSection) {
          this.#hasObservedInitialSection = true;
          this.#activeSectionId = activeId;

          // A first-time learner should still get a useful resume point. Existing bookmarks are
          // preserved until the learner actually moves to another section.
          if (!this.#readCurrentBookmark()) this.#saveBookmark(activeId);
          return;
        }

        if (activeId !== this.#activeSectionId) {
          this.#activeSectionId = activeId;
          this.#saveBookmark(activeId);
        }
      },
      { threshold: 0.4 }
    );

    for (const id of this.#sectionIds) {
      const section = this.querySelector(`#${CSS.escape(id)}`);
      if (section) this.#observer.observe(section);
    }
  }

  /** @param {string} id */
  #setActiveTocItem(id) {
    for (const item of this.querySelectorAll('.course-toc__item')) {
      item.setAttribute('aria-current', String(item.getAttribute('href') === `#${id}`));
    }
  }

  /** @param {MouseEvent} event */
  rememberSection(event) {
    const link = /** @type {HTMLAnchorElement | null} */ (event.target);
    const sectionId = link?.hash.slice(1);
    if (sectionId) this.#saveBookmark(decodeURIComponent(sectionId));
  }

  /** @param {string} sectionId */
  #saveBookmark(sectionId) {
    const section = /** @type {HTMLElement | null} */ (
      this.querySelector(`#${CSS.escape(sectionId)}`)
    );
    if (!section || !this.#moduleKey || !this.#moduleUrl) return;

    writeBookmark(this.#courseKey, {
      moduleKey: this.#moduleKey,
      moduleUrl: this.#moduleUrl,
      sectionId,
      label: sectionLabel(section),
      updatedAt: Date.now(),
    });
    this.#renderResume();
  }

  #renderResume() {
    const { resumeLink, resumeLabel } = this.refs;
    if (!resumeLink || !resumeLabel) return;

    const bookmark = this.#readCurrentBookmark();
    if (!bookmark) {
      resumeLink.hidden = true;
      return;
    }

    const moduleLink = /** @type {HTMLAnchorElement | null} */ (
      this.querySelector(`.course-sidebar__link[data-module-key="${CSS.escape(bookmark.moduleKey)}"]`)
    );
    if (!moduleLink) {
      resumeLink.hidden = true;
      return;
    }

    if (
      bookmark.moduleKey === this.#moduleKey &&
      !this.querySelector(`#${CSS.escape(bookmark.sectionId)}`)
    ) {
      resumeLink.hidden = true;
      return;
    }

    const moduleUrl = bookmark.moduleUrl.split('#')[0];
    resumeLink.href = `${moduleUrl}#${encodeURIComponent(bookmark.sectionId)}`;
    resumeLabel.textContent = bookmark.label;
    resumeLink.hidden = false;
  }

  #scheduleAccessRefresh() {
    if (!this.refs.lockedPanel) return;

    const refreshKey = `${ACCESS_REFRESH_PREFIX}${this.#courseKey}:${this.#moduleKey}`;
    try {
      if (sessionStorage.getItem(refreshKey)) return;
      sessionStorage.setItem(refreshKey, 'true');
    } catch {
      // Without session storage a reload could loop forever, so leave the manual refresh door.
      return;
    }

    this.#accessRefreshTimer = window.setTimeout(() => window.location.reload(), 15_000);
  }

  async toggleComplete() {
    if (this.#saving) return;

    const done = !this.#progress[this.#moduleKey];

    // Optimistic: paint first, reconcile after. Snapshot both objects so a failure can put the
    // tick back exactly as it was.
    const previousProgress = this.#progress;
    const previousAll = this.#allProgress;

    this.#progress = { ...this.#progress, [this.#moduleKey]: done };
    this.#allProgress = { ...this.#allProgress, [this.#courseKey]: this.#progress };
    this.#showSaveError(null);
    this.#renderProgress();

    // Headless is an optional account-sync upgrade. With no public client configured, persist the
    // same stable-ID document locally and finish synchronously.
    //
    // The theme editor takes this path too. It has no customer to write for, so it must never call
    // the Customer Account API — but it does have a browser, and localStorage works there like
    // anywhere else. Painting the tick and discarding it made Mark as Complete look broken to any
    // merchant testing in the editor.
    if (!this.#clientId || this.#isDesignMode) {
      const latest = readLocalProgress(this.#progressScope, this.#progressKeys);
      const saved = mergeProgress(latest, this.#allProgress, this.#progressKeys);
      const state = { ...(saved[this.#courseKey] ?? {}) };
      if (done) state[this.#moduleKey] = true;
      else delete state[this.#moduleKey];
      saved[this.#courseKey] = state;

      if (writeLocalProgress(this.#progressScope, saved)) {
        this.#allProgress = saved;
        this.#progress = state;
        if (done && this.dataset.moduleFree === 'true') {
          publishCourseAnalytics(this, 'free_module_completion', 'lesson_complete');
        }
        return;
      }

      this.#progress = previousProgress;
      this.#allProgress = previousAll;
      this.#renderProgress();
      this.#showSaveError('errorUnavailable');
      return;
    }

    this.#saving = true;
    try {
      const saved = await persistProgress({
        clientId: this.#clientId,
        mutate: (latest) => {
          // Merge browser-only progress before applying this click. This is the automatic upgrade
          // path when Headless is added after learners have already used the course.
          const normalized = mergeProgress(latest, this.#allProgress, this.#progressKeys);
          const state = { ...(normalized[this.#courseKey] ?? {}) };
          if (done) state[this.#moduleKey] = true;
          else delete state[this.#moduleKey];
          normalized[this.#courseKey] = state;
          return normalized;
        },
      });
      this.#allProgress = saved;
      this.#progress = saved[this.#courseKey] ?? {};
      writeLocalProgress(this.#progressScope, saved);
      this.#renderProgress();
      if (done && this.dataset.moduleFree === 'true') {
        publishCourseAnalytics(this, 'free_module_completion', 'lesson_complete');
      }
    } catch (error) {
      const code = error instanceof CustomerAccountError ? error.code : undefined;

      if (code === 'UNAUTHENTICATED') {
        // First tick of the session, or the refresh token expired. Park the intent and hand over
        // to Shopify's hosted sign-in; the callback replays it on the way back, so the click is
        // not lost. Deliberately only ever reached from a click — browsing a course never
        // redirects.
        try {
          await beginAuth({
            clientId: this.#clientId,
            pending: {
              kind: 'module-completion',
              courseKey: this.#courseKey,
              moduleKey: this.#moduleKey,
              done,
            },
          });
          return;
        } catch {
          // Fall through to the revert below: sign-in could not even be started.
        }
      }

      // No second copy anywhere — progress is only in the metafield now — so a failed write must
      // be visible, never swallowed.
      this.#progress = previousProgress;
      this.#allProgress = previousAll;
      this.#renderProgress();
      this.#showSaveError(code === 'THROTTLED' ? 'errorThrottled' : 'errorFailed');
    } finally {
      this.#saving = false;
    }
  }

  /**
   * Messages come from `data-error-*` on the element rather than from JS, so they stay in
   * locales/en.default.json like every other customer-facing string.
   *
   * @param {'errorFailed' | 'errorThrottled' | 'errorUnavailable' | null} key
   */
  #showSaveError(key) {
    const { completeError = [] } = this.refs;

    for (const element of completeError) {
      if (!element) continue;
      const message = key ? element.dataset[key] ?? '' : '';
      element.textContent = message;
      element.hidden = !message;
    }
  }

  /** Paints the progress bars, the sidebar ticks, and the complete button from in-memory state. */
  #renderProgress() {
    const state = this.#progress;

    // The module total comes from Liquid — counting sidebar links would miss modules that the
    // merchant has in the menu but hasn't linked from this page.
    const total = Number(this.dataset.moduleTotal) || 0;
    const currentKeys = this.#moduleKeys.length ? this.#moduleKeys : Object.keys(state).slice(0, total);
    const done = Math.min(currentKeys.filter((key) => state[key] === true).length, total);

    // Progress renders twice — sidebar on desktop, above the module nav on mobile — so these are
    // array refs (`ref="progressBar[]"`). Both instances paint from this one pass.
    const { progressBar = [], progressFill = [], progressLabel = [] } = this.refs;
    const completeButtons = [
      this.refs.completeButtonDesktop,
      this.refs.completeButtonInline,
    ].filter(Boolean);

    if (total > 0) {
      const percent = `${Math.round((done / total) * 100)}%`;

      for (const fill of progressFill) fill.style.width = percent;
      for (const label of progressLabel) label.textContent = `${done}/${total}`;

      for (const bar of progressBar) {
        const labelTemplate = bar.dataset.labelTemplate ?? '[done]/[total]';
        bar.setAttribute('aria-valuenow', String(done));
        bar.setAttribute(
          'aria-label',
          labelTemplate.replace('[done]', String(done)).replace('[total]', String(total))
        );
      }
    }

    for (const link of this.querySelectorAll('.course-sidebar__link')) {
      const key = /** @type {HTMLElement} */ (link).dataset.moduleKey ?? '';
      link.setAttribute('data-done', String(Boolean(state[key])));
    }

    const isDone = Boolean(state[this.#moduleKey]);

    for (const completeButton of completeButtons) {
      if (!completeButton) continue;

      completeButton.dataset.done = String(isDone);
      completeButton.setAttribute('aria-pressed', String(isDone));

      // Write into the label span, not the button: the button also holds the checkmark icon, and
      // setting textContent on it would delete the icon on the first paint.
      const label = completeButton.querySelector('[ref^="completeLabel"]') ?? completeButton;
      label.textContent = completeButton.dataset[isDone ? 'doneText' : 'todoText'] ?? label.textContent;
    }
  }
}

if (!customElements.get('course-module')) {
  customElements.define('course-module', CourseModule);
}

/**
 * Hydrates the Course Index from the same browser fallback as module and quiz pages. Liquid still
 * supplies the account copy for first paint when Headless exists; JavaScript adds local-only ticks.
 */
class CourseIndex extends Component {
  #onStorage = (event) => {
    if (event.key === localProgressKey(this.#progressScope)) this.#renderProgress();
  };

  #onAnalyticsClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-course-event]') : null;
    if (!(target instanceof HTMLElement) || !this.contains(target)) return;
    publishCourseAnalytics(this, target.dataset.courseEvent ?? '', target.dataset.coursePosition ?? '');
  };

  get #courseKey() {
    return this.dataset.courseKey ?? 'course';
  }

  get #legacyCourseKey() {
    return this.dataset.legacyCourseKey ?? '';
  }

  get #moduleKeyMap() {
    return parseModuleKeyMap(this.dataset.moduleKeyMap ?? '');
  }

  get #progressScope() {
    return this.dataset.progressScope ?? 'guest';
  }

  get #progressKeys() {
    return {
      courseKey: this.#courseKey,
      legacyCourseKey: this.#legacyCourseKey,
      moduleKeyMap: this.#moduleKeyMap,
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.#renderProgress();
    window.addEventListener('storage', this.#onStorage);
    this.addEventListener('click', this.#onAnalyticsClick);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#renderProgress(), { once: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    window.removeEventListener('storage', this.#onStorage);
    this.removeEventListener('click', this.#onAnalyticsClick);
  }

  updatedCallback() {
    super.updatedCallback?.();
    this.#renderProgress();
  }

  #renderProgress() {
    const server = parseProgress(this.dataset.progress ?? '');
    const local = readLocalProgress(this.#progressScope, this.#progressKeys);
    const all = mergeProgress(server, local, this.#progressKeys);
    const state = all[this.#courseKey] ?? {};
    let done = 0;

    for (const row of this.querySelectorAll('[data-course-index-module]')) {
      if (!(row instanceof HTMLElement)) continue;
      const key = row.dataset.moduleKey ?? '';
      const canOpen = row.dataset.canOpen === 'true';
      const isDone = canOpen && state[key] === true;
      if (isDone) done += 1;

      const number = row.querySelector('[data-course-index-number]');
      const check = row.querySelector('[data-course-index-check]');
      const lock = row.querySelector('[data-course-index-lock]');
      const includedTag = row.querySelector('[data-course-index-included]');
      const completeTag = row.querySelector('[data-course-index-complete-tag]');
      const marker = row.querySelector('.course-index__num');

      if (number instanceof HTMLElement) number.hidden = isDone || !canOpen;
      if (check instanceof HTMLElement) check.hidden = !isDone;
      if (lock instanceof HTMLElement) lock.hidden = canOpen;
      if (includedTag instanceof HTMLElement) includedTag.hidden = isDone;
      if (completeTag instanceof HTMLElement) completeTag.hidden = !isDone;
      marker?.classList.toggle('course-index__num--done', isDone);
    }

    const stat = this.querySelector('[data-course-index-progress-stat]');
    if (!(stat instanceof HTMLElement)) return;
    const total = Number(stat.dataset.total) || 0;
    const number = stat.querySelector('[data-course-index-progress-number]');
    const label = stat.querySelector('[data-course-index-progress-label]');

    if (number) number.textContent = done > 0 ? `${Math.min(done, total)}/${total}` : String(total);
    if (label) {
      label.textContent = stat.dataset[done > 0 ? 'completedLabel' : 'modulesLabel'] ?? '';
    }
  }
}

if (!customElements.get('course-index')) {
  customElements.define('course-index', CourseIndex);
}

/**
 * Course quiz — the final challenge on the course-complete page.
 *
 * Questions are server-rendered from metaobjects; this handles selection, scoring and the write.
 * The correct answer ships in `data-correct`, which is fine and was settled by the design: a
 * browser-scored quiz is self-assessment, not a credential. A learner who reads the DOM to cheat
 * has only fooled themselves, and the merchant never relies on the number.
 *
 * The score is stored at the OUTER level of the progress blob under `quiz:<courseKey>`, NOT inside
 * the course's module map:
 *
 *   { "seo-system": { "seo-01-what-seo-is": true },
 *     "quiz:seo-system": { "score": 6, "total": 8 } }
 *
 * Nesting it beside the modules was the original plan and would mix two unrelated record types.
 * Keeping it under a reserved outer key also preserves compatibility with older progress JSON.
 */
/**
 * @typedef {object} QuizRefs
 * @property {HTMLButtonElement} [scoreButton]
 * @property {HTMLElement} [resultPanel]
 * @property {HTMLElement} [resultScore]
 * @property {HTMLElement} [resultMessage]
 * @property {HTMLElement} [previousScore]
 * @property {HTMLElement[]} [quizError]
 */

/** @extends {Component<QuizRefs>} */
class CourseQuiz extends Component {
  /** @type {Map<number, number>} question index -> chosen option index */
  #answers = new Map();
  #scored = false;
  #saving = false;
  #allProgress = {};
  #onStorage = (event) => {
    if (event.key !== localProgressKey(this.#progressScope)) return;
    this.#hydrateProgress();
  };

  get #clientId() {
    return this.dataset.accountClientId ?? '';
  }

  get #courseKey() {
    return this.dataset.courseKey ?? 'course';
  }

  get #legacyCourseKey() {
    return this.dataset.legacyCourseKey ?? '';
  }

  get #moduleKeyMap() {
    return parseModuleKeyMap(this.dataset.moduleKeyMap ?? '');
  }

  get #progressScope() {
    return this.dataset.progressScope ?? 'guest';
  }

  get #progressKeys() {
    return {
      courseKey: this.#courseKey,
      legacyCourseKey: this.#legacyCourseKey,
      moduleKeyMap: this.#moduleKeyMap,
    };
  }

  /** @returns {HTMLElement[]} */
  get #questions() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.course-quiz__q')]);
  }

  connectedCallback() {
    super.connectedCallback();
    this.#hydrateProgress();
    this.#syncScoreButton();
    window.addEventListener('storage', this.#onStorage);

    // Custom elements can upgrade when their opening tag is parsed, before the questions exist.
    // Consuming the OAuth replay at that point would discard it as invalid. Wait for the document
    // parser exactly as CourseModule does for its DOM-derived controls.
    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        () => {
          this.#syncScoreButton();
          this.#renderPreviousScore();
          this.#replayPendingQuiz();
        },
        { once: true }
      );
    } else {
      this.#replayPendingQuiz();
    }
  }

  updatedCallback() {
    super.updatedCallback?.();
    this.#renderPreviousScore();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    window.removeEventListener('storage', this.#onStorage);
  }

  #hydrateProgress() {
    const server = parseProgress(this.dataset.progress ?? '');
    const local = readLocalProgress(this.#progressScope, this.#progressKeys);
    this.#allProgress = mergeProgress(server, local, this.#progressKeys);
    this.dataset.progress = JSON.stringify(this.#allProgress);
    this.#renderPreviousScore();
  }

  #renderPreviousScore() {
    const element = this.refs.previousScore;
    if (!element) return;

    const previous = this.#allProgress[QUIZ_KEY_PREFIX + this.#courseKey];
    const score = Number(previous?.score);
    const total = Number(previous?.total);
    if (!Number.isFinite(score) || !Number.isFinite(total) || total <= 0) {
      element.hidden = true;
      return;
    }

    const template = element.dataset.template ?? '[score]/[total]';
    element.textContent = template
      .replace('[score]', String(score))
      .replace('[total]', String(total));
    element.hidden = false;
  }

  /**
   * Restores the answers that caused the first Customer Account API sign-in. Scoring happens
   * before the API call, so without replay the OAuth redirect would preserve neither the answers
   * nor the score and the learner would have to take the quiz again.
   */
  #replayPendingQuiz() {
    let pending;

    try {
      const raw = sessionStorage.getItem(REPLAY_KEY);
      if (!raw) return;
      sessionStorage.removeItem(REPLAY_KEY);
      pending = JSON.parse(raw);
    } catch {
      return;
    }

    if (pending?.kind !== 'quiz' || pending?.courseKey !== this.#courseKey) return;
    if (!Array.isArray(pending.answers)) return;

    const questions = this.#questions;
    if (!questions.length || pending.answers.length !== questions.length) return;

    /** @type {Map<number, { optionIndex: number, option: Element }>} */
    const restored = new Map();
    for (const answer of pending.answers) {
      if (!Array.isArray(answer) || answer.length !== 2) return;
      const [questionIndex, optionIndex] = answer.map(Number);
      if (!Number.isInteger(questionIndex) || !Number.isInteger(optionIndex)) return;
      if (questionIndex < 0 || questionIndex >= questions.length || restored.has(questionIndex)) return;

      const option = questions[questionIndex].querySelector(
        `.course-quiz__opt[data-index="${optionIndex}"]`
      );
      if (!option) return;
      restored.set(questionIndex, { optionIndex, option });
    }

    if (restored.size !== questions.length) return;
    for (const [questionIndex, { optionIndex, option }] of restored) {
      this.#answers.set(questionIndex, optionIndex);
      option.setAttribute('aria-pressed', 'true');
    }

    this.#syncScoreButton();
    this.scoreQuiz();
  }

  /** @param {MouseEvent} event */
  selectOption(event) {
    if (this.#scored) return;

    const target = /** @type {HTMLElement} */ (event.target);
    const option = /** @type {HTMLElement | null} */ (target.closest('.course-quiz__opt'));
    const question = option?.closest('.course-quiz__q');
    if (!option || !(question instanceof HTMLElement)) return;

    const qIndex = this.#questions.indexOf(question);
    const oIndex = Number(option.dataset.index);
    if (qIndex < 0 || Number.isNaN(oIndex)) return;

    for (const sibling of question.querySelectorAll('.course-quiz__opt')) {
      sibling.setAttribute('aria-pressed', String(sibling === option));
    }

    this.#answers.set(qIndex, oIndex);
    this.#syncScoreButton();
  }

  #syncScoreButton() {
    const { scoreButton } = this.refs;
    if (!scoreButton) return;
    scoreButton.disabled = this.#answers.size < this.#questions.length;
  }

  async scoreQuiz() {
    if (this.#scored || this.#saving) return;

    const questions = this.#questions;
    let score = 0;

    questions.forEach((question, index) => {
      const correct = Number(question.dataset.correct);
      const chosen = this.#answers.get(index);
      if (chosen === correct) score += 1;

      for (const option of question.querySelectorAll('.course-quiz__opt')) {
        const optionIndex = Number(/** @type {HTMLElement} */ (option).dataset.index);
        // Always mark the right answer, so a wrong pick teaches rather than only scolds.
        if (optionIndex === correct) option.setAttribute('data-state', 'correct');
        else if (optionIndex === chosen) option.setAttribute('data-state', 'wrong');
        option.setAttribute('aria-disabled', 'true');
      }

      const explanation = question.querySelector('.course-quiz__explain');
      if (explanation instanceof HTMLElement) explanation.hidden = false;
    });

    this.#scored = true;
    this.#paintResult(score, questions.length);
    await this.#persistScore(score, questions.length, [...this.#answers.entries()]);
  }

  /**
   * @param {number} score
   * @param {number} total
   */
  #paintResult(score, total) {
    const { scoreButton, resultPanel, resultScore, resultMessage } = this.refs;

    if (scoreButton) scoreButton.hidden = true;
    if (resultScore) resultScore.textContent = score + '/' + total;

    if (resultMessage) {
      // Bands are keyed off the RATIO, not the absolute score, so the message still makes sense if
      // a course ships a quiz of a different length. Copy lives in data attributes so it stays in
      // locales/en.default.json like every other customer-facing string.
      const ratio = total > 0 ? score / total : 0;
      let key = 'bandLow';
      if (ratio === 1) key = 'bandPerfect';
      else if (ratio >= 0.75) key = 'bandStrong';
      else if (ratio >= 0.5) key = 'bandFair';
      resultMessage.textContent = resultMessage.dataset[key] ?? '';
    }

    if (resultPanel) {
      resultPanel.hidden = false;
      resultPanel.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /**
   * @param {number} score
   * @param {number} total
   * @param {Array<[number, number]>} answers
   */
  async #persistScore(score, total, answers) {
    const quizKey = QUIZ_KEY_PREFIX + this.#courseKey;

    if (!this.#clientId) {
      const latest = readLocalProgress(this.#progressScope, this.#progressKeys);
      const saved = mergeProgress(latest, this.#allProgress, this.#progressKeys);
      saved[quizKey] = { score, total };

      if (!writeLocalProgress(this.#progressScope, saved)) {
        this.#showError('errorUnavailable');
        return;
      }

      this.#allProgress = saved;
      this.dataset.progress = JSON.stringify(saved);
      this.#renderPreviousScore();
      this.#showError(null);
      return;
    }

    this.#saving = true;
    this.#showError(null);
    try {
      const saved = await persistProgress({
        clientId: this.#clientId,
        mutate: (latest) => {
          const normalized = mergeProgress(latest, this.#allProgress, this.#progressKeys);
          normalized[quizKey] = { score, total };
          return normalized;
        },
      });
      // Keep the element's own copy current, so a retake in the same session writes on top of this
      // result rather than on top of the value Liquid rendered at page load.
      this.#allProgress = saved;
      this.dataset.progress = JSON.stringify(saved);
      writeLocalProgress(this.#progressScope, saved);
      this.#renderPreviousScore();
    } catch (error) {
      const code = error instanceof CustomerAccountError ? error.code : undefined;

      if (code === 'UNAUTHENTICATED') {
        try {
          await beginAuth({
            clientId: this.#clientId,
            pending: { kind: 'quiz', courseKey: this.#courseKey, answers },
          });
          return;
        } catch {
          // Fall through — sign-in could not even be started.
        }
      }

      // The score stays on screen. Only the SAVE failed, and saying so is honest: wiping a result
      // the learner just earned because a network call failed is worse than not storing it.
      this.#showError(code === 'THROTTLED' ? 'errorThrottled' : 'errorFailed');
    } finally {
      this.#saving = false;
    }
  }

  retryQuiz() {
    for (const question of this.#questions) {
      for (const option of question.querySelectorAll('.course-quiz__opt')) {
        option.removeAttribute('data-state');
        option.removeAttribute('aria-disabled');
        option.setAttribute('aria-pressed', 'false');
      }
      const explanation = question.querySelector('.course-quiz__explain');
      if (explanation instanceof HTMLElement) explanation.hidden = true;
    }

    this.#answers.clear();
    this.#scored = false;

    const { scoreButton, resultPanel } = this.refs;
    if (resultPanel) resultPanel.hidden = true;
    if (scoreButton) scoreButton.hidden = false;
    this.#syncScoreButton();
    this.#showError(null);
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /** @param {'errorFailed' | 'errorThrottled' | 'errorUnavailable' | null} key */
  #showError(key) {
    for (const element of this.refs.quizError ?? []) {
      if (!element) continue;
      const message = key ? element.dataset[key] ?? '' : '';
      element.textContent = message;
      element.hidden = !message;
    }
  }
}

if (!customElements.get('course-quiz')) {
  customElements.define('course-quiz', CourseQuiz);
}
